/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const INIT_CONFIG_DISCRIMINATOR = new Uint8Array([
  97, 166, 35, 7, 20, 2, 164, 126,
]);

export function getInitConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(INIT_CONFIG_DISCRIMINATOR);
}

export type InitConfig = {
  discriminator: ReadonlyUint8Array;
  admin: Address;
  migratorAllowlistLen: number;
  migratorAllowlist: Array<Address>;
  hookAllowlistLen: number;
  hookAllowlist: Array<Address>;
  bump: number;
  version: number;
  protocolFeeBps: number;
  minSwapFeeBps: number;
  maxSwapFeeBps: number;
  reserved: ReadonlyUint8Array;
};

export type InitConfigArgs = {
  admin: Address;
  migratorAllowlistLen: number;
  migratorAllowlist: Array<Address>;
  hookAllowlistLen: number;
  hookAllowlist: Array<Address>;
  bump: number;
  version: number;
  protocolFeeBps: number;
  minSwapFeeBps: number;
  maxSwapFeeBps: number;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link InitConfigArgs} account data. */
export function getInitConfigEncoder(): FixedSizeEncoder<InitConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['admin', getAddressEncoder()],
      ['migratorAllowlistLen', getU8Encoder()],
      ['migratorAllowlist', getArrayEncoder(getAddressEncoder(), { size: 32 })],
      ['hookAllowlistLen', getU8Encoder()],
      ['hookAllowlist', getArrayEncoder(getAddressEncoder(), { size: 32 })],
      ['bump', getU8Encoder()],
      ['version', getU8Encoder()],
      ['protocolFeeBps', getU16Encoder()],
      ['minSwapFeeBps', getU16Encoder()],
      ['maxSwapFeeBps', getU16Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 24)],
    ]),
    (value) => ({ ...value, discriminator: INIT_CONFIG_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link InitConfig} account data. */
export function getInitConfigDecoder(): FixedSizeDecoder<InitConfig> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['admin', getAddressDecoder()],
    ['migratorAllowlistLen', getU8Decoder()],
    ['migratorAllowlist', getArrayDecoder(getAddressDecoder(), { size: 32 })],
    ['hookAllowlistLen', getU8Decoder()],
    ['hookAllowlist', getArrayDecoder(getAddressDecoder(), { size: 32 })],
    ['bump', getU8Decoder()],
    ['version', getU8Decoder()],
    ['protocolFeeBps', getU16Decoder()],
    ['minSwapFeeBps', getU16Decoder()],
    ['maxSwapFeeBps', getU16Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 24)],
  ]);
}

/** Gets the codec for {@link InitConfig} account data. */
export function getInitConfigCodec(): FixedSizeCodec<
  InitConfigArgs,
  InitConfig
> {
  return combineCodec(getInitConfigEncoder(), getInitConfigDecoder());
}

export function decodeInitConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<InitConfig, TAddress>;
export function decodeInitConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<InitConfig, TAddress>;
export function decodeInitConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<InitConfig, TAddress> | MaybeAccount<InitConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getInitConfigDecoder(),
  );
}

export async function fetchInitConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<InitConfig, TAddress>> {
  const maybeAccount = await fetchMaybeInitConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeInitConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<InitConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeInitConfig(maybeAccount);
}

export async function fetchAllInitConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<InitConfig>[]> {
  const maybeAccounts = await fetchAllMaybeInitConfig(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeInitConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<InitConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeInitConfig(maybeAccount));
}

export function getInitConfigSize(): number {
  return 2122;
}
